import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { toast } from 'react-toastify';
import BillingNav from './BillingNav';
import { BillingPage } from './billingUi';
import { fetchRenewals, proposeRenewal, renewalAction } from '../../services/billingApi';
import { renewCarryForward, renewCarryForwardBulk, computeRenewalPeriods } from '../../services/licensingApi';

/** Spec — Renewals: renew dialog with carry-forward option + bulk + propose workflow */
const BillingRenewals = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ proposedEndDate: '', quantityChanges: '', pricingChanges: '', proposedNotes: '' });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [renewTarget, setRenewTarget] = useState(null); // null | 'bulk' | row
  const [carryForward, setCarryForward] = useState(true);
  const [renewPeriods, setRenewPeriods] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await fetchRenewals());
      setSelected(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectableIds = useMemo(
    () => rows.map((r) => String(r.subscriptionId)).filter(Boolean),
    [rows]
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableIds));
    }
  };

  const toggleOne = (subscriptionId) => {
    const id = String(subscriptionId);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedTenantIds = useMemo(() => {
    const ids = [];
    for (const r of rows) {
      if (selected.has(String(r.subscriptionId)) && r.tenantId != null) {
        ids.push(Number(r.tenantId));
      }
    }
    return [...new Set(ids)];
  }, [rows, selected]);

  const openRenewDialog = (target) => {
    setCarryForward(true);
    setRenewTarget(target);
    if (target && target !== 'bulk') {
      setRenewPeriods(
        computeRenewalPeriods({
          startDate: target.startDate,
          endDate: target.endDate,
          billingCycle: target.billingCycle,
        })
      );
    } else {
      setRenewPeriods(null);
    }
  };

  const closeRenewDialog = () => {
    if (busy) return;
    setRenewTarget(null);
    setRenewPeriods(null);
  };

  const act = async (renewalId, action) => {
    setBusy(true);
    try {
      await renewalAction(renewalId, action);
      toast.success(action === 'RENEW' ? 'Renewal applied' : `Renewal ${action.toLowerCase()}d`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitPropose = async () => {
    if (!open?.subscriptionId || !form.proposedEndDate) {
      toast.error('Proposed end date is required');
      return;
    }
    setBusy(true);
    try {
      await proposeRenewal({ subscriptionId: open.subscriptionId, ...form });
      toast.success('Renewal proposed');
      setOpen(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmRenew = async () => {
    if (!renewTarget) return;

    if (!carryForward) {
      if (renewTarget === 'bulk') {
        toast.info('Uncheck carry forward only for a single tenant — use Propose, or Tenants → Subscriptions → Renew.');
        return;
      }
      setRenewTarget(null);
      setOpen(renewTarget);
      setForm({
        proposedEndDate: renewTarget.proposedEndDate
          ? String(renewTarget.proposedEndDate).slice(0, 10)
          : renewTarget.endDate
            ? String(renewTarget.endDate).slice(0, 10)
            : '',
        quantityChanges: renewTarget.quantityChanges || '',
        pricingChanges: renewTarget.pricingChanges || '',
        proposedNotes: renewTarget.proposedNotes || '',
      });
      toast.info('Carry forward off — propose custom changes, or open the tenant Subscriptions drawer to Add a term.');
      return;
    }

    setBusy(true);
    try {
      if (renewTarget === 'bulk') {
        if (selectedTenantIds.length === 0) {
          toast.info('Select at least one tenant');
          return;
        }
        const result = await renewCarryForwardBulk(selectedTenantIds, { createdBy: 'cdx' });
        const ok = result.succeeded ?? 0;
        const fail = result.failed ?? 0;
        if (fail === 0) {
          toast.success(`Renewed ${ok} tenant${ok === 1 ? '' : 's'}`);
        } else {
          toast.warning(`Renewed ${ok}, failed ${fail}`);
        }
      } else {
        const row = renewTarget;
        if (!row?.tenantId) {
          toast.error('Missing tenant for this row');
          return;
        }
        const created = await renewCarryForward(row.tenantId, {
          sourceSubscriptionId: row.subscriptionId,
          createdBy: 'cdx',
        });
        toast.success(
          `Renewed ${row.tenantName || row.tenantId} · License # ${created.licenseNumber || created.subscriptionId}`
        );
      }
      setRenewTarget(null);
      await load();
    } catch (e) {
      toast.error(e.message || 'Renew failed');
    } finally {
      setBusy(false);
    }
  };

  const renewCount =
    renewTarget === 'bulk' ? selectedTenantIds.length : renewTarget ? 1 : 0;

  return (
    <BillingPage>
      <BillingNav
        title="Renewals"
        subtitle="Renew opens a dialog with an option to carry forward license settings. Select rows for bulk renew, or use Propose when terms need review."
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="contained"
          size="small"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <AutorenewIcon />}
          disabled={busy || selectedTenantIds.length === 0}
          onClick={() => openRenewDialog('bulk')}
        >
          Renew selected ({selectedTenantIds.length})
        </Button>
        <Button size="small" disabled={busy || rows.length === 0} onClick={toggleAll}>
          {allSelected ? 'Clear selection' : 'Select all'}
        </Button>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={toggleAll}
                  disabled={rows.length === 0}
                />
              </TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>License #</TableCell>
              <TableCell>Term end / renewal</TableCell>
              <TableCell>Billing cycle</TableCell>
              <TableCell>Auto-renew</TableCell>
              <TableCell>Subscription status</TableCell>
              <TableCell>Renewal status</TableCell>
              <TableCell>Proposed end</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography color="text.secondary">No upcoming renewals.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.subscriptionId} selected={selected.has(String(r.subscriptionId))}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.has(String(r.subscriptionId))}
                      onChange={() => toggleOne(r.subscriptionId)}
                    />
                  </TableCell>
                  <TableCell>{r.tenantName}</TableCell>
                  <TableCell>{r.planName || '—'}</TableCell>
                  <TableCell>{r.licenseNumber || '—'}</TableCell>
                  <TableCell>
                    {r.endDate ? String(r.endDate).slice(0, 10) : '—'}
                  </TableCell>
                  <TableCell>{r.billingCycle || '—'}</TableCell>
                  <TableCell>{r.autoRenew ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.subscriptionStatus} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.renewalStatus} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {r.proposedEndDate ? String(r.proposedEndDate).slice(0, 10) : '—'}
                  </TableCell>
                  <TableCell>
                    <Button size="small" disabled={busy} onClick={() => openRenewDialog(r)}>
                      Renew
                    </Button>
                    <Button size="small" onClick={() => { setOpen(r); setForm({
                      proposedEndDate: r.proposedEndDate ? String(r.proposedEndDate).slice(0, 10) : '',
                      quantityChanges: r.quantityChanges || '',
                      pricingChanges: r.pricingChanges || '',
                      proposedNotes: r.proposedNotes || '',
                    }); }}>
                      Propose
                    </Button>
                    {r.renewalId && r.renewalStatus === 'IN_REVIEW' && (
                      <Button size="small" disabled={busy} onClick={() => act(r.renewalId, 'APPROVE')}>Approve</Button>
                    )}
                    {r.renewalId && (r.renewalStatus === 'APPROVED' || r.renewalStatus === 'IN_REVIEW') && (
                      <Button size="small" disabled={busy} onClick={() => act(r.renewalId, 'RENEW')}>Apply</Button>
                    )}
                    {r.renewalId && !['RENEWED', 'DECLINED'].includes(r.renewalStatus) && (
                      <Button size="small" color="warning" disabled={busy} onClick={() => act(r.renewalId, 'DECLINE')}>Decline</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Propose renewal</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" type="date" label="Proposed end date" InputLabelProps={{ shrink: true }}
            sx={{ mt: 1 }} value={form.proposedEndDate}
            onChange={(e) => setForm((f) => ({ ...f, proposedEndDate: e.target.value }))} />
          <TextField fullWidth size="small" label="Quantity changes" sx={{ mt: 2 }} value={form.quantityChanges}
            onChange={(e) => setForm((f) => ({ ...f, quantityChanges: e.target.value }))} />
          <TextField fullWidth size="small" label="Pricing changes" sx={{ mt: 2 }} value={form.pricingChanges}
            onChange={(e) => setForm((f) => ({ ...f, pricingChanges: e.target.value }))} />
          <TextField fullWidth size="small" multiline minRows={2} label="Notes" sx={{ mt: 2 }} value={form.proposedNotes}
            onChange={(e) => setForm((f) => ({ ...f, proposedNotes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={submitPropose}>Submit</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!renewTarget} onClose={closeRenewDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {renewTarget === 'bulk'
            ? `Renew ${renewCount} tenant${renewCount === 1 ? '' : 's'}?`
            : 'Renew subscription'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {renewTarget === 'bulk'
              ? 'Creates a new term and invoice for each selected tenant. Next term starts after each current end date (no overlap). No email until Invoices → Send email….'
              : `Renew ${renewTarget?.tenantName || 'tenant'}. If renewed early, the new term starts after the current period ends. No email until Invoices → Send email….`}
          </Typography>
          {renewTarget !== 'bulk' && renewPeriods && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Current active period
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                {renewPeriods.currentStart || '—'} → {renewPeriods.currentEnd || '—'}
                {renewPeriods.stillActive ? ' (still active)' : ' (ended / expired)'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Next term (no overlap) · {renewPeriods.billingCycle || renewTarget?.billingCycle || '—'}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {renewPeriods.nextStart || '—'} → {renewPeriods.nextEnd || '—'}
              </Typography>
              {renewPeriods.stillActive && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Current term stays in force until {renewPeriods.currentEnd}. New term becomes current after that.
                </Typography>
              )}
            </Box>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={carryForward}
                onChange={(e) => setCarryForward(e.target.checked)}
                disabled={busy}
              />
            }
            label="Carry forward all existing license settings"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
            {carryForward
              ? 'Products, entitlements, connectors, flows, and LLMs stay as-is. Dates follow the next term above.'
              : renewTarget === 'bulk'
                ? 'Bulk renew requires carry forward. For custom terms, renew one tenant at a time or use Propose.'
                : 'Opens Propose so you can set custom end date / quantity / pricing changes.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenewDialog} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || (renewTarget === 'bulk' && !carryForward)}
            onClick={confirmRenew}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <AutorenewIcon />}
          >
            {busy ? 'Renewing…' : carryForward ? 'Renew' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </BillingPage>
  );
};

export default BillingRenewals;
